import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'DE' | 'EN';

interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const translations: Record<Language, Record<string, string>> = {
  DE: {
    // Header
    'nav.startseite': 'Startseite',
    'nav.alleServices': 'Alle Services',
    'nav.smartphones': 'Smartphones',
    'nav.handytarife': 'Handytarife',
    'nav.internet': 'Internet',
    'nav.stromGas': 'Strom & Gas',
    'nav.blog': 'Blog',
    'nav.kontakt': 'Kontakt',
    'nav.anrufen': 'Anrufen',
    'nav.kontaktAufnehmen': 'Kontakt aufnehmen',
    'nav.suchen': 'Suchen',
    'nav.searchPlaceholder': 'Suchen Sie nach Smartphones, Tarifen, Anbietern...',
    'nav.lokaleBeratung': 'Lokale Beratung im Saarland:',
    'nav.mobilfunk': 'Mobilfunk',
    'nav.vertraegeMitOhne': 'Verträge mit & ohne Handy',
    'nav.alleAnbieter': 'Alle Anbieter vergleichen',
    'nav.internetZuhause': 'Internet zuhause',
    'nav.dslKabelGlasfaser': 'DSL, Kabel & Glasfaser',
    'nav.lokaleAnbieter': 'Lokale Anbieter',
    'nav.bis400Sparen': 'Bis 400€ sparen',
    'nav.lokaleStromtarife': 'Lokale Stromtarife',
    'nav.geraeteZubehoer': 'Geräte & Zubehör',
    'nav.tabletsWatchesAudio': 'Tablets, Watches, Audio',
    'nav.versicherung': 'Versicherung',
    'nav.versicherungsvergleiche': 'Versicherungsvergleiche',
    'nav.blogRatgeber': 'Blog & Ratgeber',
    'nav.tippsVergleiche': 'Tipps & Vergleiche',
    'nav.ueberUns': 'Über uns',
    'nav.lokalerPartner': 'Ihr lokaler Partner',
    'nav.tarifrechner': 'Tarifrechner',
    'nav.persoenlichenTarif': 'Persönlichen Tarif finden',
    'nav.internetEnergie': 'Internet & Energie',
    'nav.geraeteService': 'Geräte & Service',
    // Hero
    'hero.badge': 'Lokale Beratung in Völklingen · Saarland',
    'hero.title1': 'Vergleichen Sie.',
    'hero.tw1': 'Sparen',
    'hero.tw2': 'Abschließen',
    'hero.tw3': 'Vergleichen',
    'hero.subtitle': 'Ihr lokaler Partner im Saarland für Handyverträge, Smartphones, Internet und Energie. Vodafone, O2 & Sparhandy kostenlos vergleichen. Jetzt Sparen',
    'hero.searchPlaceholder': 'Was suchen Sie? z.B. iPhone 16, DSL, Strom...',
    'hero.searchBtn': 'Suchen',
    'hero.ctaTarif': 'Tarifrechner starten',
    'hero.ctaWhatsapp': 'WhatsApp Beratung',
    'hero.trust.customers': 'Kunden',
    'hero.trust.rating': 'Bewertung',
    'hero.trust.dsgvo': 'Konform',
    'hero.trust.sparen': 'Sparen',
    // Trust bar
    'trustbar.kostenlos': 'Kostenlos & Unverbindlich',
    'trustbar.kostenlosDesc': 'Vergleiche 100% gratis',
    'trustbar.exklusive': 'Exklusive Deals',
    'trustbar.exklusiveDesc': 'Sonderkonditionen der Anbieter',
    'trustbar.beratung': 'Persönliche Beratung',
    'trustbar.beratungDesc': 'Lokal im Saarland',
    'trustbar.schnell': 'Schnelle Abwicklung',
    'trustbar.schnellDesc': 'In unter 5 Minuten',
    // Homepage sections
    'home.featuredBadge': 'Top Angebote',
    'home.featuredTitle': 'Empfohlene Angebote',
    'home.featuredDesc': 'Die besten Deals von Vodafone, O2 und Sparhandy – exklusiv für unsere Kunden.',
    'home.alleAngebote': 'Alle Angebote ansehen',
    'home.comparisonBadge': 'Tarifvergleich',
    'home.comparisonTitle': 'Finden Sie den perfekten Mobilfunktarif',
    'home.comparisonDesc': 'Vergleichen Sie die besten Angebote von Vodafone, O2 und Sparhandy und sparen Sie bis zu 50% mit unseren exklusiven Affiliate-Deals.',
    'home.tarifeVergleichen': 'Tarife vergleichen',
    'home.affiliateBadge': 'Unsere Partner-Programme',
    'home.affiliateTitle': 'Direkt abschließen – bei unseren Partnern',
    'home.affiliateDesc': 'Wählen Sie einen Anbieter und schließen Sie Ihren Vertrag direkt ab. Sie erhalten exklusive Konditionen und wir kümmern uns um den Rest.',
    'home.affiliateCta': 'Zum Anbieter',
    'home.affiliateMore': 'Alle Programme anzeigen',
    'home.localBadge': 'Lokal & Regional',
    'home.localTitle': 'Handyvertrag-Beratung in Völklingen & Saarland',
    'home.localDesc1': 'Easy Vertrag ist in Völklingen (Bismarck Straße 31) ansässig und betreut Kunden aus dem gesamten Saarland. Wir beraten Sie persönlich vor Ort, per WhatsApp oder telefonisch.',
    'home.localDesc2': 'Unsere Kunden kommen aus Völklingen, Saarbrücken, Saarlouis, Dillingen, Wadgassen und der gesamten Saar-Region.',
    'home.localCta': 'Jetzt Beratung anfordern',
    'home.whyBadge': 'Warum Easy Vertrag',
    'home.whyTitle': 'Ihr lokaler Mobilfunk-Partner in Völklingen',
    'home.whyDesc': 'Easy Vertrag ist Ihr persönlicher Ansprechpartner im Saarland für Handyverträge, Smartphones, Internet und Energietarife. Wir vergleichen für Sie die Angebote aller führenden Anbieter.',
    'home.why1': 'Kostenlose und unverbindliche Beratung',
    'home.why2': 'Alle Anbieter im direkten Vergleich',
    'home.why3': 'Exklusive Aktionsangebote & Cashbacks',
    'home.why4': 'Persönlicher Service via WhatsApp & E-Mail',
    'home.whyCta': 'Mehr über uns',
    'home.servicesBadge': 'Unsere Leistungen',
    'home.servicesTitle': 'Was bietet Easy Vertrag?',
    'home.servicesDesc': 'Von Smartphones über Mobilfunktarife bis zu Internet und Energie – wir sind Ihr Komplettanbieter für alle Vertragsthemen.',
    'home.localPagesTitle': 'Angebote für Ihr Gebiet im Saarland',
    'home.localPagesDesc': 'Speziell für Völklinger und Saarland-Kunden – vergleichen Sie lokale Angebote für Handyvertrag, Internet und Energie.',
    'home.faqBadge': 'FAQ',
    'home.faqTitle': 'Häufig gestellte Fragen',
    'home.faqDesc': 'Antworten auf die wichtigsten Fragen rund um Mobilfunk und Technik.',
    'home.blogBadge': 'Ratgeber & Tipps',
    'home.blogTitle': 'Aus unserem Blog',
    'home.blogDesc': 'Aktuelle Vergleiche, Spartipps und Ratgeber zu Handyverträgen, Smartphones und mehr.',
    'home.blogCta': 'Alle Artikel ansehen',
    'home.leadBadge': 'Kostenlose Beratung',
    'home.leadTitle': 'Jetzt kostenlos beraten lassen',
    'home.leadDesc': 'Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden mit dem besten Angebot für Sie.',
    'home.leadFormTitle': 'Beratungsanfrage',
    'home.leadFormSubtitle': 'Wir finden den besten Tarif für Ihre Bedürfnisse.',
    'home.weiterlesen': 'Weiterlesen',
    'home.mehrErfahren': 'Mehr erfahren',
  },
  EN: {
    // Header
    'nav.startseite': 'Home',
    'nav.alleServices': 'All Services',
    'nav.smartphones': 'Smartphones',
    'nav.handytarife': 'Mobile Plans',
    'nav.internet': 'Internet',
    'nav.stromGas': 'Electricity & Gas',
    'nav.blog': 'Blog',
    'nav.kontakt': 'Contact',
    'nav.anrufen': 'Call',
    'nav.kontaktAufnehmen': 'Get in touch',
    'nav.suchen': 'Search',
    'nav.searchPlaceholder': 'Search for smartphones, plans, providers...',
    'nav.lokaleBeratung': 'Local advice in Saarland:',
    'nav.mobilfunk': 'Mobile',
    'nav.vertraegeMitOhne': 'Contracts with & without phone',
    'nav.alleAnbieter': 'Compare all providers',
    'nav.internetZuhause': 'Home internet',
    'nav.dslKabelGlasfaser': 'DSL, cable & fiber',
    'nav.lokaleAnbieter': 'Local providers',
    'nav.bis400Sparen': 'Save up to €400',
    'nav.lokaleStromtarife': 'Local electricity rates',
    'nav.geraeteZubehoer': 'Devices & Accessories',
    'nav.tabletsWatchesAudio': 'Tablets, watches, audio',
    'nav.versicherung': 'Insurance',
    'nav.versicherungsvergleiche': 'Insurance comparisons',
    'nav.blogRatgeber': 'Blog & Guides',
    'nav.tippsVergleiche': 'Tips & comparisons',
    'nav.ueberUns': 'About us',
    'nav.lokalerPartner': 'Your local partner',
    'nav.tarifrechner': 'Plan calculator',
    'nav.persoenlichenTarif': 'Find your personal plan',
    'nav.internetEnergie': 'Internet & Energy',
    'nav.geraeteService': 'Devices & Service',
    // Hero
    'hero.badge': 'Local advice in Völklingen · Saarland',
    'hero.title1': 'Compare.',
    'hero.tw1': 'Save',
    'hero.tw2': 'Sign up',
    'hero.tw3': 'Compare',
    'hero.subtitle': 'Your local partner in Saarland for mobile contracts, smartphones, internet and energy. Compare Vodafone, O2 & Sparhandy for free. Save now',
    'hero.searchPlaceholder': 'What are you looking for? e.g. iPhone 16, DSL, electricity...',
    'hero.searchBtn': 'Search',
    'hero.ctaTarif': 'Start plan calculator',
    'hero.ctaWhatsapp': 'WhatsApp consultation',
    'hero.trust.customers': 'Customers',
    'hero.trust.rating': 'Rating',
    'hero.trust.dsgvo': 'Compliant',
    'hero.trust.sparen': 'Save',
    // Trust bar
    'trustbar.kostenlos': 'Free & Non-binding',
    'trustbar.kostenlosDesc': '100% free comparisons',
    'trustbar.exklusive': 'Exclusive deals',
    'trustbar.exklusiveDesc': 'Special provider conditions',
    'trustbar.beratung': 'Personal advice',
    'trustbar.beratungDesc': 'Local in Saarland',
    'trustbar.schnell': 'Fast processing',
    'trustbar.schnellDesc': 'In under 5 minutes',
    // Homepage sections
    'home.featuredBadge': 'Top offers',
    'home.featuredTitle': 'Recommended offers',
    'home.featuredDesc': 'The best deals from Vodafone, O2 and Sparhandy – exclusively for our customers.',
    'home.alleAngebote': 'View all offers',
    'home.comparisonBadge': 'Plan comparison',
    'home.comparisonTitle': 'Find the perfect mobile plan',
    'home.comparisonDesc': 'Compare the best offers from Vodafone, O2 and Sparhandy and save up to 50% with our exclusive affiliate deals.',
    'home.tarifeVergleichen': 'Compare plans',
    'home.affiliateBadge': 'Our partner programs',
    'home.affiliateTitle': 'Sign up directly – with our partners',
    'home.affiliateDesc': 'Choose a provider and sign up for your contract directly. You get exclusive conditions and we take care of the rest.',
    'home.affiliateCta': 'Go to provider',
    'home.affiliateMore': 'Show all programs',
    'home.localBadge': 'Local & Regional',
    'home.localTitle': 'Mobile contract advice in Völklingen & Saarland',
    'home.localDesc1': 'Easy Vertrag is based in Völklingen (Bismarck Straße 31) and serves customers from all over Saarland. We advise you personally on site, via WhatsApp or by phone.',
    'home.localDesc2': 'Our customers come from Völklingen, Saarbrücken, Saarlouis, Dillingen, Wadgassen and the entire Saar region.',
    'home.localCta': 'Request advice now',
    'home.whyBadge': 'Why Easy Vertrag',
    'home.whyTitle': 'Your local mobile partner in Völklingen',
    'home.whyDesc': 'Easy Vertrag is your personal contact in Saarland for mobile contracts, smartphones, internet and energy tariffs. We compare offers from all leading providers for you.',
    'home.why1': 'Free and non-binding advice',
    'home.why2': 'All providers in direct comparison',
    'home.why3': 'Exclusive promotional offers & cashbacks',
    'home.why4': 'Personal service via WhatsApp & email',
    'home.whyCta': 'More about us',
    'home.servicesBadge': 'Our services',
    'home.servicesTitle': 'What does Easy Vertrag offer?',
    'home.servicesDesc': 'From smartphones to mobile plans to internet and energy – we are your one-stop shop for all contract matters.',
    'home.localPagesTitle': 'Offers for your area in Saarland',
    'home.localPagesDesc': 'Especially for Völklingen and Saarland customers – compare local offers for mobile contracts, internet and energy.',
    'home.faqBadge': 'FAQ',
    'home.faqTitle': 'Frequently asked questions',
    'home.faqDesc': 'Answers to the most important questions about mobile and technology.',
    'home.blogBadge': 'Tips & Guides',
    'home.blogTitle': 'From our blog',
    'home.blogDesc': 'Current comparisons, money-saving tips and guides on mobile contracts, smartphones and more.',
    'home.blogCta': 'View all articles',
    'home.leadBadge': 'Free consultation',
    'home.leadTitle': 'Get free advice now',
    'home.leadDesc': 'Fill out the form and we will get back to you within 24 hours with the best offer for you.',
    'home.leadFormTitle': 'Consultation request',
    'home.leadFormSubtitle': 'We find the best plan for your needs.',
    'home.weiterlesen': 'Read more',
    'home.mehrErfahren': 'Learn more',
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('easy-vertrag-lang');
    return (saved === 'EN' || saved === 'DE') ? saved : 'DE';
  });

  useEffect(() => {
    localStorage.setItem('easy-vertrag-lang', lang);
    document.documentElement.lang = lang === 'DE' ? 'de' : 'en';
  }, [lang]);

  const setLang = (newLang: Language) => setLangState(newLang);

  const t = (key: string): string => {
    return translations[lang][key] ?? translations.DE[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
